import { ButtonVariants, ControlPrefix, FormControlSizes, HStack, ToggleGroup } from '@talos/kyoko';
import { ASSET_CONTROL_WIDTH, METRIC_CONTROL_WIDTH } from '../core';
import { AggregatedAssetSelect } from './AggregatedAssetSelect';
import { AGGREGATION_METHOD_OPTIONS, type AggregationMethod } from './AggregatedMetric.types';
import { AggregatedMetricSelect } from './AggregatedMetricSelect';

interface AggregatedMetricToolbarProps {
  /** Active metric id (e.g. `'CapMrktEstUSD'`). */
  metric: string;
  /** Selectable metrics — intersection supported by the selected assets. */
  availableMetrics: string[];
  /** Catalog query loading state (drives the metric picker's disabled/loading copy). */
  isMetricsLoading: boolean;
  /** Catalog query error state (drives the metric picker's disabled/error copy). */
  isMetricsError: boolean;
  onMetricChange: (metric: string) => void;
  /** Maps a metric ID to its display label. */
  getMetricLabel: (metricId: string) => string;
  /** Currently selected assets as Talos symbols (e.g. `['BTC','ETH']`). */
  selectedTalosSymbols: string[];
  /** All Talos symbols available for the active metric (the asset checklist options). */
  availableTalosSymbols: string[];
  /** Fires live on every asset check/uncheck — the parent persists immediately. */
  onSelectedTalosSymbolsChange: (talosSymbols: string[]) => void;
  /** Active Sum/Mean aggregation applied across the selected assets. */
  aggregationMethod: AggregationMethod;
  /** Fires when the user flips Sum/Mean — the parent persists and re-aggregates live. */
  onAggregationMethodChange: (method: AggregationMethod) => void;
}

/**
 * Chart-view toolbar controls: a Sum/Mean aggregation toggle plus a compact asset multi-select
 * and metric picker that mirror the settings panel and re-plot live. They share the panel's
 * state, so editing here or in settings is equivalent; the settings cog remains the entry to
 * the full panel (title, etc.).
 */
export function AggregatedMetricToolbar({
  metric,
  availableMetrics,
  isMetricsLoading,
  isMetricsError,
  onMetricChange,
  getMetricLabel,
  selectedTalosSymbols,
  availableTalosSymbols,
  onSelectedTalosSymbolsChange,
  aggregationMethod,
  onAggregationMethodChange,
}: AggregatedMetricToolbarProps) {
  return (
    <HStack gap="spacing8">
      <ToggleGroup
        value={aggregationMethod}
        defaultSelectedVariant={ButtonVariants.Priority}
        options={AGGREGATION_METHOD_OPTIONS}
        onChange={onAggregationMethodChange}
        size={FormControlSizes.Small}
        data-testid="aggregated-metric-toolbar-aggregation"
      />
      <AggregatedAssetSelect
        availableTalosSymbols={availableTalosSymbols}
        selectedTalosSymbols={selectedTalosSymbols}
        onSelectedTalosSymbolsChange={onSelectedTalosSymbolsChange}
        width={ASSET_CONTROL_WIDTH}
        buttonTestID="aggregated-metric-toolbar-assets"
        dropdownTestID="aggregated-metric-toolbar-assets-dropdown"
      />
      <AggregatedMetricSelect
        metric={metric}
        availableMetrics={availableMetrics}
        isLoading={isMetricsLoading}
        isError={isMetricsError}
        hasSelectedAssets={selectedTalosSymbols.length > 0}
        onMetricChange={onMetricChange}
        getLabel={getMetricLabel}
        prefix={<ControlPrefix>Metric</ControlPrefix>}
        width={METRIC_CONTROL_WIDTH}
        testID="aggregated-metric-toolbar-metric"
      />
    </HStack>
  );
}
